import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  MenuButton,
  Stack,
  VStack,
} from "@chakra-ui/react";
import { Card, CardHeader, LoadingOverlay } from "@components/ui-kit";
import { NotFoundClient } from "@components/widgets/ErrorPage";
import { Trans, t } from "@lingui/macro";
import {
  useClientDetailQuery,
  useUpdateClientMutation,
} from "@src/__generated__/graphql";
import { PageLoading } from "@src/components/modules/clients/page-loading";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { Icon } from "@src/components/ui-kit/Icon";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { NavigationClient } from "@src/components/widgets/AppSubNav";
import { onError } from "@src/utils/apollo";
import { can, withCan } from "@src/utils/components/permissions";
import { headTitle } from "@src/utils/head-title";
import { getQueryParam } from "@src/utils/routing";
import { UserSelect } from "@src/widgets/UserSelect";
import { observer } from "mobx-react-lite";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { CardBillingAddresses } from "./CardBillingAddresses";
import { CardBrands } from "./CardBrands";
import { CardExchangeRates } from "./CardExchangeRates";
import { PopoverClientActions } from "./PopoverClientActions";
import { Store } from "./Store";

export const PageClientDetailCtx = React.createContext<Store>(
  null as any as Store,
);

export const PageClientDetail = withCan(
  { do: "client_read" },
  observer(function PageClientDetail() {
    const { query } = useRouter();
    const clientId = getQueryParam("id", query);
    const router = useRouter();

    const [store] = useState(() => {
      const store = Store.create();
      return store;
    });

    const { data, refetch, loading } = useClientDetailQuery({
      variables: { id: clientId },
      skip: !clientId,
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        // TODO Fix date
        // @ts-expect-error
        store.fromClientDetailQuery(data);
      },
    });

    const [update] = useUpdateClientMutation({ ...onError() });

    const onSelectResponsiblePerson = (val: string) => {
      store.form.responsible_person_id.onChange(val);
      update({
        variables: {
          input: {
            name: client.name,
            responsible_person_user_id: val,
            id: client.id,
            workspace_company_id: client.workspaceCompany.id,
          },
        },
      });
    };

    const onSelectOtherResponsiblePersons = (val: string[]) => {
      store.form.other_responsible_persons_ids.onChange(val);
      update({
        variables: {
          input: {
            name: client.name,
            other_responsible_person_user_ids: val,
            id: client.id,
            workspace_company_id: client.workspaceCompany.id,
          },
        },
      });
    };

    // TODO Fix date
    // @ts-expect-error
    store.refetch = refetch;

    if (loading) {
      return <PageLoading clientId={clientId} />;
    }

    if (!data?.client) {
      return <NotFoundClient />;
    }

    if (!store.client) {
      return null;
    }

    const { client } = store;

    return (
      <React.Fragment>
        <Head>
          <title>{headTitle(client.name)}</title>
        </Head>
        <PageClientDetailCtx.Provider value={store}>
          <Card>
            <HStack
              justifyContent="space-between"
              px="4"
              borderBottom="1px"
              borderBottomColor="gray.200"
            >
              <NavigationClient id={clientId} variant="page" />

              <HStack spacing="2">
                {!!client.cloudUrl && <SharepointLink href={client.cloudUrl} />}
                {!!client.driveCloudUrl && (
                  <GoogleDriveLink href={client.driveCloudUrl} />
                )}
                {can("client_delete", "client_update") && (
                  <PopoverClientActions
                    client={client}
                    onAfterDelete={() => {
                      router.back();
                    }}
                    trigger={
                      <MenuButton
                        as={Button}
                        colorScheme="grey"
                        rightIcon={
                          <Icon name="chevron-down" color="grey.300" />
                        }
                        variant="outline"
                      >
                        <Trans>Actions</Trans>
                      </MenuButton>
                    }
                  />
                )}
              </HStack>
            </HStack>

            <LoadingOverlay
              transparent={false}
              minH="50dvh"
              isLoading={loading}
            >
              <Flex direction={{ base: "column", md: "row" }} h="fit-content">
                <VStack
                  align="start"
                  flex="1"
                  borderRight="1px"
                  borderRightColor="gray.200"
                  spacing="4"
                >
                  <CardBrands />

                  <CardBillingAddresses />

                  <Box w="full">
                    <CardHeader
                      icon="building-03"
                      title={
                        <Heading size="md">
                          <Trans>Legal entity</Trans>
                        </Heading>
                      }
                    />
                    <Card.Section>{client.workspaceCompany.name}</Card.Section>
                  </Box>
                </VStack>

                <Stack flex="1" h="full" spacing="2">
                  <CardExchangeRates />

                  <Box>
                    <CardHeader
                      icon="user-02"
                      title={
                        <Heading size="md">
                          <Trans>Responsible manager</Trans>
                        </Heading>
                      }
                    />
                    <Card.Section>
                      <UserSelect
                        placeholder={t`Select project manager`}
                        label={""}
                        onChange={onSelectResponsiblePerson}
                        value={store.form.responsible_person_id.$}
                      />
                    </Card.Section>
                  </Box>

                  <Box>
                    <CardHeader
                      icon="user-02"
                      title={
                        <Heading size="md">
                          <Trans>Other managers</Trans>
                        </Heading>
                      }
                    />
                    <Card.Section>
                      <UserSelect
                        isMulti
                        placeholder={t`Select project manager`}
                        label={""}
                        onChange={onSelectOtherResponsiblePersons}
                        value={store.form.other_responsible_persons_ids.$}
                      />
                    </Card.Section>
                  </Box>
                </Stack>
              </Flex>
            </LoadingOverlay>
          </Card>
        </PageClientDetailCtx.Provider>
      </React.Fragment>
    );
  }),
);
